import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SEOHead } from '../../components/SEOHead';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { CheckCircle, AlertCircle, Loader2, Building2, Mail } from 'lucide-react';

interface FormData {
  id: string;
  title: string;
  description: string;
  button_text: string;
  success_title: string;
  success_message: string;
  confirmation_required: boolean;
  gdpr_enabled: boolean;
  gdpr_text: string;
  gdpr_privacy_url: string | null;
}

interface UserData {
  company_name: string;
  username_slug: string;
  website_url: string | null;
}

interface BlogSettings {
  logo_url: string | null;
  accent_color: string;
}

type PageStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'not_found';

export function SubscribeForm() {
  const { username } = useParams<{ username: string }>();
  const [searchParams] = useSearchParams();
  const confirmToken = searchParams.get('token');

  const [pageStatus, setPageStatus] = useState<PageStatus>('loading');
  const [formData, setFormData] = useState<FormData | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [blogSettings, setBlogSettings] = useState<BlogSettings | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [gdprChecked, setGdprChecked] = useState(false);
  const [gdprError, setGdprError] = useState('');
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);

  useEffect(() => {
    if (confirmToken) {
      loadBrandData().then(() => handleConfirmToken(confirmToken));
    } else {
      loadForm();
    }
  }, [username, confirmToken]);

  const loadBrandData = async () => {
    if (!username) return;
    try {
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('id, company_name, username_slug, website_url')
        .eq('username_slug', username)
        .maybeSingle();

      if (userError || !user) return;

      setUserData({ company_name: user.company_name, username_slug: user.username_slug, website_url: user.website_url });

      const { data: settings } = await supabase
        .from('user_blog_settings')
        .select('logo_url, accent_color')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settings) setBlogSettings(settings);
    } catch {
      // silently fail, brand data is non-critical
    }
  };

  const handleConfirmToken = async (token: string) => {
    setPageStatus('submitting');
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/confirm-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
        },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();
      if (data.success) {
        setPageStatus('success');
        setConfirmationSent(false);
      } else {
        setPageStatus('error');
        setErrorMessage(data.error || 'De bevestigingslink is ongeldig of verlopen.');
      }
    } catch {
      setPageStatus('error');
      setErrorMessage('Er is een fout opgetreden. Probeer het later opnieuw.');
    }
  };

  const loadForm = async () => {
    if (!username) return;
    try {
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('id, company_name, username_slug, website_url')
        .eq('username_slug', username)
        .maybeSingle();

      if (userError || !user) {
        setPageStatus('not_found');
        return;
      }

      setUserData({ company_name: user.company_name, username_slug: user.username_slug, website_url: user.website_url });

      const [formRes, settingsRes] = await Promise.all([
        supabase
          .from('subscription_forms')
          .select('id, title, description, button_text, success_title, success_message, confirmation_required, gdpr_enabled, gdpr_text, gdpr_privacy_url')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('user_blog_settings')
          .select('logo_url, accent_color')
          .eq('user_id', user.id)
          .maybeSingle(),
      ]);

      const defaultForm: FormData = {
        id: '',
        title: `Schrijf je in voor de nieuwsbrief van ${user.company_name}`,
        description: 'Blijf op de hoogte van de laatste blogs en updates.',
        button_text: 'Inschrijven',
        success_title: 'Bedankt voor je inschrijving!',
        success_message: 'Controleer je inbox voor een bevestigingsemail.',
        confirmation_required: true,
        gdpr_enabled: false,
        gdpr_text: 'Ik ga akkoord met de verwerking van mijn gegevens voor het ontvangen van de nieuwsbrief.',
        gdpr_privacy_url: null,
      };

      setFormData(formRes.data ?? defaultForm);
      if (settingsRes.data) setBlogSettings(settingsRes.data);
      setPageStatus('ready');
    } catch {
      setPageStatus('error');
      setErrorMessage('Er is een fout opgetreden bij het laden van het formulier.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setGdprError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Voer een geldig emailadres in');
      return;
    }

    if (formData?.gdpr_enabled && !gdprChecked) {
      setGdprError('Je moet akkoord gaan om je in te schrijven.');
      return;
    }

    setPageStatus('submitting');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/subscribe-to-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
        },
        body: JSON.stringify({ username, email, name }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.already_subscribed) {
          setAlreadySubscribed(true);
        } else if (data.confirmation_sent) {
          setConfirmationSent(true);
        }
        setPageStatus('success');
      } else {
        setPageStatus('ready');
        setErrorMessage(data.error || 'Er ging iets mis. Probeer het later opnieuw.');
      }
    } catch {
      setPageStatus('ready');
      setErrorMessage('Er is een fout opgetreden. Probeer het later opnieuw.');
    }
  };

  const accentColor = blogSettings?.accent_color || '#FF6B35';

  if (pageStatus === 'loading') {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <LoadingSpinner text="Formulier laden..." />
      </div>
    );
  }

  if (pageStatus === 'not_found') {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-md w-full text-center shadow-sm">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Formulier niet gevonden</h1>
          <p className="text-gray-600">Dit inschrijfformulier is niet beschikbaar.</p>
        </div>
      </div>
    );
  }

  const pageTitle = formData?.title || `Inschrijven nieuwsbrief ${userData?.company_name || ''}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={formData?.description || ''}
        url={`https://lees-nu.nl/inschrijven/${username}`}
        type="website"
      />

      <div className="min-h-screen bg-[#F2F2EF]" style={{ '--accent-color': accentColor } as React.CSSProperties}>
        <main className="max-w-lg mx-auto px-4 py-16">
          <div className="flex flex-col items-center mb-8">
            {blogSettings?.logo_url ? (
              <img
                src={blogSettings.logo_url}
                alt="Logo"
                className="max-h-[72px] max-w-[160px] w-auto h-auto object-contain"
              />
            ) : (
              <Building2 className="w-12 h-12" style={{ color: accentColor }} />
            )}
          </div>

          {pageStatus === 'submitting' && !confirmToken && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
              <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: accentColor }} />
              <p className="text-gray-600">Even geduld...</p>
            </div>
          )}

          {pageStatus === 'submitting' && confirmToken && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
              <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: accentColor }} />
              <p className="text-gray-600">Bevestiging verwerken...</p>
            </div>
          )}

          {pageStatus === 'success' && confirmToken && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: `${accentColor}15` }}>
                <CheckCircle className="w-9 h-9" style={{ color: accentColor }} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Inschrijving bevestigd!</h1>
              <p className="text-gray-600 mb-8">Je bent nu ingeschreven. Je ontvangt voortaan emails van {userData?.company_name || 'deze afzender'}.</p>
              {userData?.website_url && (
                <a
                  href={/^https?:\/\//i.test(userData.website_url) ? userData.website_url : `https://${userData.website_url}`}
                  className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-opacity hover:opacity-90"
                  style={{ backgroundColor: accentColor }}
                >
                  Naar de website
                </a>
              )}
            </div>
          )}

          {pageStatus === 'success' && !confirmToken && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: `${accentColor}15` }}>
                {confirmationSent ? (
                  <Mail className="w-9 h-9" style={{ color: accentColor }} />
                ) : (
                  <CheckCircle className="w-9 h-9" style={{ color: accentColor }} />
                )}
              </div>
              {alreadySubscribed ? (
                <>
                  <h1 className="text-2xl font-bold text-gray-900 mb-3">Al ingeschreven</h1>
                  <p className="text-gray-600">Je bent al ingeschreven op deze nieuwsbrief.</p>
                </>
              ) : confirmationSent ? (
                <>
                  <h1 className="text-2xl font-bold text-gray-900 mb-3">{formData?.success_title || 'Bedankt!'}</h1>
                  <p className="text-gray-600 mb-2">{formData?.success_message || 'Controleer je inbox voor een bevestigingsemail.'}</p>
                  <p className="text-sm text-gray-500">Klik op de link in de email om je inschrijving te bevestigen.</p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-gray-900 mb-3">{formData?.success_title || 'Bedankt!'}</h1>
                  <p className="text-gray-600">{formData?.success_message || 'Je bent succesvol ingeschreven.'}</p>
                </>
              )}
            </div>
          )}

          {pageStatus === 'error' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
                <AlertCircle className="w-9 h-9 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Er is iets misgegaan</h1>
              <p className="text-gray-600">{errorMessage}</p>
            </div>
          )}

          {pageStatus === 'ready' && formData && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-8 pt-8 pb-6 border-b border-gray-100">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">{formData.title}</h1>
                {formData.description && (
                  <p className="text-gray-600 leading-relaxed">{formData.description}</p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
                {errorMessage && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Voornaam <span className="text-gray-400 font-normal">(optioneel)</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jan"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-colors focus:border-transparent focus:ring-2"
                    style={{ '--tw-ring-color': accentColor } as React.CSSProperties}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Emailadres <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    placeholder="jouw@email.nl"
                    required
                    className={`w-full px-4 py-3 border rounded-lg outline-none transition-colors focus:border-transparent focus:ring-2 ${emailError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    style={{ '--tw-ring-color': accentColor } as React.CSSProperties}
                  />
                  {emailError && (
                    <p className="text-sm text-red-600 mt-1">{emailError}</p>
                  )}
                </div>

                {formData.gdpr_enabled && (
                  <div>
                    <label className={`flex items-start gap-3 cursor-pointer group ${gdprError ? 'text-red-700' : ''}`}>
                      <input
                        type="checkbox"
                        checked={gdprChecked}
                        onChange={(e) => { setGdprChecked(e.target.checked); setGdprError(''); }}
                        className="mt-0.5 w-4 h-4 rounded border-gray-300 flex-shrink-0"
                        style={{ accentColor }}
                      />
                      <span className={`text-sm leading-relaxed ${gdprError ? 'text-red-700' : 'text-gray-600'}`}>
                        {formData.gdpr_privacy_url ? (
                          <>
                            {formData.gdpr_text.replace(/\.$/, '')}{' '}
                            <a
                              href={/^https?:\/\//i.test(formData.gdpr_privacy_url) ? formData.gdpr_privacy_url : `https://${formData.gdpr_privacy_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:opacity-80"
                              style={{ color: accentColor }}
                            >
                              Lees onze privacyverklaring.
                            </a>
                          </>
                        ) : (
                          formData.gdpr_text
                        )}
                      </span>
                    </label>
                    {gdprError && (
                      <p className="text-sm text-red-600 mt-1 ml-7">{gdprError}</p>
                    )}
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-opacity hover:opacity-90 active:opacity-80"
                    style={{ backgroundColor: accentColor }}
                  >
                    {formData.button_text}
                  </button>
                </div>

                {formData.confirmation_required && (
                  <p className="text-xs text-gray-500 text-center">
                    Je ontvangt een bevestigingsemail. Je kunt je altijd uitschrijven.
                  </p>
                )}
              </form>
            </div>
          )}
        </main>

        <footer className="py-8 text-center">
          <p className="text-sm text-gray-500">
            Mogelijk gemaakt door{' '}
            <a
              href="https://www.dibbi.nl"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: accentColor }}
            >
              dibbi
            </a>
          </p>
        </footer>
      </div>
    </>
  );
}
